import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Languages, Bell, UserCircle2, ChevronRight, Loader2, MoonStar, MailCheck, Eye } from 'lucide-react';
import PushNotificationManager from '../components/PushNotificationManager';

const DEFAULT_PREFERENCES = {
  dark_mode: false,
  email_alerts: true,
  newsletter_enabled: false,
  visibility_mode: 'restricted',
  preferred_language: null,
};

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, updatePreferences } = useAuth();
  const { t, language, setLanguage, supportedLanguages } = useLanguage();
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    setPreferences({
      ...DEFAULT_PREFERENCES,
      ...(user?.preferences || {}),
    });
  }, [user]);

  useEffect(() => {
    const preferredLanguage = user?.preferences?.preferred_language;
    if (preferredLanguage && preferredLanguage !== language) {
      setLanguage(preferredLanguage);
    }
  }, [user?.preferences?.preferred_language, language, setLanguage]);

  const getInitials = (name) => {
    return name?.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase() || 'U';
  };

  const savePreferences = async (partialUpdate, key) => {
    const previous = preferences;
    const next = {
      ...preferences,
      ...partialUpdate,
    };

    setPreferences(next);
    setSavingKey(key);

    try {
      await updatePreferences(next);
    } catch (error) {
      setPreferences(previous);
      toast.error(error.response?.data?.detail || t('settingsPage.saveError'));
    } finally {
      setSavingKey(null);
    }
  };

  const renderSavingIndicator = (key) => {
    if (savingKey !== key) {
      return null;
    }

    return <Loader2 className="h-4 w-4 animate-spin text-orange-600" />;
  };

  const handleLanguageChange = async (nextLanguage) => {
    if (!nextLanguage || nextLanguage === language) {
      return;
    }

    setLanguage(nextLanguage);
    await savePreferences({ preferred_language: nextLanguage }, 'preferred_language');
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-stone-900 dark:text-stone-50">{t('settings.title')}</h1>
        <p className="text-stone-500 mt-1">{t('settingsPage.subtitle')}</p>
      </div>

      <Card className="card-neo">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCircle2 className="w-5 h-5 text-orange-600" />
            {t('settingsPage.profileTitle')}
          </CardTitle>
          <CardDescription>{t('settingsPage.profileDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16 ring-4 ring-orange-100 dark:ring-orange-900/40">
              <AvatarImage src={user?.avatar_url || undefined} alt={user?.full_name || 'User profile'} />
              <AvatarFallback className="bg-orange-100 text-lg font-semibold text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                {getInitials(user?.full_name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-stone-900 dark:text-stone-100">{user?.full_name}</p>
              <p className="text-sm text-stone-500">{user?.email}</p>
            </div>
          </div>

          <Button variant="outline" onClick={() => navigate('/profile')}>
            {t('settingsPage.editProfile')}
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </CardContent>
      </Card>

      <Card className="card-neo">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Languages className="w-5 h-5 text-orange-600" />
            {t('settingsPage.languageTitle')}
          </CardTitle>
          <CardDescription>{t('settingsPage.languageDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="app-language">{t('settingsPage.languageLabel')}</Label>
            <Select value={language} onValueChange={handleLanguageChange}>
              <SelectTrigger id="app-language" className="max-w-xs" data-testid="settings-language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {supportedLanguages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.flag} {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="card-neo">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-orange-600" />
            {t('settingsPage.notificationsTitle')}
          </CardTitle>
          <CardDescription>{t('settingsPage.notificationsDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <PushNotificationManager variant="full" />
        </CardContent>
      </Card>

      <Card className="card-neo">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MoonStar className="w-5 h-5 text-orange-600" />
            {t('settingsPage.preferencesTitle')}
          </CardTitle>
          <CardDescription>{t('settingsPage.preferencesDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-xl border border-stone-200/80 bg-stone-50/80 p-4 dark:border-stone-800 dark:bg-stone-900/40">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <MoonStar className="h-4 w-4 text-orange-600" />
                <p className="font-medium text-stone-900 dark:text-stone-100">{t('settingsPage.darkModeTitle')}</p>
              </div>
              <p className="text-sm text-stone-500">{t('settingsPage.darkModeDesc')}</p>
            </div>
            <div className="flex items-center gap-3">
              {renderSavingIndicator('dark_mode')}
              <Switch
                checked={preferences.dark_mode}
                onCheckedChange={(checked) => savePreferences({ dark_mode: checked }, 'dark_mode')}
                aria-label={t('settingsPage.darkModeTitle')}
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 rounded-xl border border-stone-200/80 bg-stone-50/80 p-4 dark:border-stone-800 dark:bg-stone-900/40">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <MailCheck className="h-4 w-4 text-orange-600" />
                <p className="font-medium text-stone-900 dark:text-stone-100">{t('settingsPage.emailAlertsTitle')}</p>
              </div>
              <p className="text-sm text-stone-500">{t('settingsPage.emailAlertsDesc')}</p>
            </div>
            <div className="flex items-center gap-3">
              {renderSavingIndicator('email_alerts')}
              <Switch
                checked={preferences.email_alerts}
                onCheckedChange={(checked) => savePreferences({ email_alerts: checked }, 'email_alerts')}
                aria-label={t('settingsPage.emailAlertsTitle')}
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 rounded-xl border border-stone-200/80 bg-stone-50/80 p-4 dark:border-stone-800 dark:bg-stone-900/40">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-orange-600" />
                <p className="font-medium text-stone-900 dark:text-stone-100">{t('settingsPage.newsletterTitle')}</p>
              </div>
              <p className="text-sm text-stone-500">{t('settingsPage.newsletterDesc')}</p>
            </div>
            <div className="flex items-center gap-3">
              {renderSavingIndicator('newsletter_enabled')}
              <Switch
                checked={preferences.newsletter_enabled}
                onCheckedChange={(checked) => savePreferences({ newsletter_enabled: checked }, 'newsletter_enabled')}
                aria-label={t('settingsPage.newsletterTitle')}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="card-neo">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-orange-600" />
            {t('settingsPage.privacyTitle')}
          </CardTitle>
          <CardDescription>{t('settingsPage.privacyDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label htmlFor="visibility-mode">{t('settingsPage.visibilityLabel')}</Label>
          <div className="flex items-center gap-3">
            <Select
              value={preferences.visibility_mode}
              onValueChange={(value) => savePreferences({ visibility_mode: value }, 'visibility_mode')}
            >
              <SelectTrigger id="visibility-mode" className="max-w-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="restricted">{t('settingsPage.visibilityOptions.restricted')}</SelectItem>
                <SelectItem value="transparent">{t('settingsPage.visibilityOptions.transparent')}</SelectItem>
              </SelectContent>
            </Select>
            {renderSavingIndicator('visibility_mode')}
          </div>
          <p className="text-sm text-stone-500">
            {preferences.visibility_mode === 'transparent'
              ? t('settingsPage.visibilityOptions.transparentDesc')
              : t('settingsPage.visibilityOptions.restrictedDesc')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
